import React, { useContext, useState } from 'react'
import { Element } from 'react-scroll'
import { menuAncresEnum } from '../enums/menuAncresEnum'
import { OffreModel } from '../types/OffreModel'
import { FormattedMessage } from 'react-intl'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import styles from '../../../styles'
import thousandsSeparator from '../../../utils/formatter/thousandsSeparator'
import { fontFamilyMonserrat } from '../../../theme'
import Tooltip from '../../../components/tooltip/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SvgDropDown from '../../../icons/SvgDropdown'
import InfoIcon from '@mui/icons-material/Info'
import { makeStyles } from 'tss-react/mui'
import { lotTypesEnum } from '../../../enums/lot/lotTypesEnum'
import { Grid } from '@mui/material'

const useStyles = makeStyles<{ isLargeScreen: boolean, estParking: boolean }>()(
	(theme, { isLargeScreen, estParking }) => (
		{
			...styles(theme),
			titre: {
				...styles(theme).title0,
				fontSize: isLargeScreen ? 35 : 22,
				marginTop: estParking ? 0 : isLargeScreen ? 79 : 59,
				marginBottom: isLargeScreen ? 28 : 24,
				lineHeight: '55px'
			},
			roundBlock: {
				border: '1px solid #EFF3F5',
				borderRadius: 30
			},
			roundBlockContent: {
				paddingLeft: 25,
				paddingRight: 15
			},
			titreHonoraires: {
				font: 'normal normal 600 15px/25px Montserrat',
				paddingLeft: 10,
				marginBottom: 10
			},
			titleLoyerMensuel: {
				font: 'normal normal 600 15px/25px Montserrat',
				marginTop: 0
			},
			titleLoyerMensuelRight: {
				font: 'normal normal 600 15px/25px Montserrat',
				textAlign: 'end'
			},
			prixValeur: {
				...styles(theme).text19,
				fontSize: 23,
				lineHeight: '33px'
			},
			dont: {
				textDecoration: 'underline',
				font: 'normal normal 600 15px/25px Montserrat',
				color: '#8895A1',
				marginTop: 8
			},
			greyText: {
				font: 'normal normal 600 15px/25px Montserrat',
				color: '#8895A1'
			},
			greyTextFin: {
				font: 'normal normal 400 15px/25px Montserrat',
				color: '#8895A1'
			},
			greyTextRight: {
				font: 'normal normal 600 15px/25px Montserrat',
				color: '#8895A1',
				textAlign: 'end'
			},
			greyTextTitle: {
				font: 'normal normal 600 15px/25px Montserrat',
				color: '#8895A1',
				marginBottom: isLargeScreen ? 15 : 0,
				maxHeight: isLargeScreen ? 24 : 52,
				'& .tooltipOuvertSVG': {
					color: theme.palette.blue.main
				}
			},
			provisionsCharges: {
				marginTop: 15,
				paddingLeft: 25,
				paddingRight: 15
			},
			modeReglement: {
				paddingLeft: 25,
				paddingRight: 15,
				marginBottom: 25
			},
			etatDesLieux: {
				marginTop: 8,
				paddingLeft: 25,
				paddingRight: 15
			},
			roundBlockContentTop: {
				paddingTop: 18,
				paddingLeft: 25,
				paddingRight: 15,
				marginBottom: 13
			},
			roundBlockContentBottom: {
				paddingLeft: 25,
				paddingRight: 15,
				marginTop: 20
			},
			blocEncadrement: {
				borderTop: '1px solid #EFF3F5',
				paddingTop: 5
			},
			referenceLoyer: {
				marginTop: 20,
				marginBottom: 20,
				paddingLeft: 25,
				paddingRight: 15,
				flexDirection: 'row'
			},
			loyerBase: {
				marginBottom: isLargeScreen ? 0 : 15
			},
			titleLoyerMajore: {
				marginBottom: isLargeScreen ? 15 : 0,
				font: 'normal normal 600 15px/25px Montserrat',
				maxHeight: isLargeScreen ? 24 : 52
			},
			toolTip: {
				position: 'relative',
				left: -25,
				'& > div': {
					width: 245,
					left: isLargeScreen ? 'inherit' : '27px !important'
				}
			},
			blocHonoraires: {
				borderBottom: '1px solid #EFF3F5',
				borderTop: '1px solid #EFF3F5',
				paddingTop: 20,
				paddingBottom: 25
			},
			dropdownButton: {
				alignSelf: 'center'
			},
			roundBlockMainContainer: {
				paddingBottom: 20
			},
			reverseButton: {
				'& svg': {
					transform: 'rotate(180deg)'
				}
			},
			chargesComprises: {
				fontWeight: 500,
				fontFamily: fontFamilyMonserrat,
				fontSize: isLargeScreen ? 12 : 10,
				lineHeight: isLargeScreen ? '15px' : '13px',
				margin: 0
			},
			autoWidth: {
				width: 'auto'
			},
			greyTextAuto: {
				font: 'normal normal 600 15px/25px Montserrat',
				color: '#8895A1',
				width: 'auto'
			}
		}
	)
)

interface AcceptingProps {
	offre: OffreModel
}

type ConditionTarifairesProps = AcceptingProps

const ConditionsTarifaires: React.FC<ConditionTarifairesProps> = (
	{
		offre
	}
) => {
	const tarification = offre.tarification
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles({ isLargeScreen, estParking: offre.type === lotTypesEnum.parking })
	const [open, setOpen] = useState<boolean>(typeof window === 'undefined')
	const [toolTipOpen, setToolTipOpen] = useState<boolean>(false)

	const dropDownButtonClass = cx(
		classes.dropdownButton,
		!open && classes.reverseButton
	)

	return (
		<Element name={menuAncresEnum.LOYER} style={{ width: '100%' }}>
			<Grid container item xs={12}>
				<Grid item container>
					<h2 className={classes.titre}>
						<FormattedMessage
							id="offre.tarification.titre"
							defaultMessage="Condition tarifaires"
							description="Titre des conditions tarifaires"
						/>
					</h2>
				</Grid>
				<Grid item container className={classes.roundBlock} md={10} xs={12}>
					<Grid item container className={classes.roundBlockMainContainer}>
						<Grid
							item
							container
							className={classes.roundBlockContentTop}
							justifyContent="space-between"
							wrap="nowrap"
						>
							<Grid item>
								<h3 className={classes.titleLoyerMensuel}>
									<FormattedMessage
										id="offre.tarification.mensuel"
										defaultMessage="Le règlement mensuel est de :"
										description="Loyer par mois"
									/>
								</h3>

								<span className={classes.prixValeur}>
									<FormattedMessage
										id="offre.tarification.montant"
										defaultMessage="{montant} €"
										description="Valeur du prix du loyer"
										values={{
											montant: <span>{thousandsSeparator(tarification.prixMensuel, false)} </span>
										}}
									/>
								</span> {' '}

								<span className={classes.chargesComprises}>
									<FormattedMessage
										id="offre.tarification.chargesComprises"
										defaultMessage="Charges comprises"
										description="Charges comprises"
									/>
								</span>
							</Grid>

							<Grid item className={dropDownButtonClass} onClick={() => setOpen(!open)}>
								<SvgDropDown />
							</Grid>
						</Grid>
						{open && <Grid item container>
							<Grid container item className={classes.roundBlockContent}>
							<span className={classes.dont}>
								<FormattedMessage
									id="offre.tarification.dont"
									defaultMessage="Dont"
									description="dont"
								/>
							</span>
							</Grid>
							<Grid container item justifyContent="space-between" className={classes.provisionsCharges}>
								<Grid item className={classes.greyText} md={9} xs={8}>
									<FormattedMessage
										id="offre.tarification.provisionCharges"
										defaultMessage="Provisions sur charges"
										description="Provisions sur charges"
									/>
								</Grid>
								<Grid item className={classes.greyTextRight} md={3} xs={4}>
									<FormattedMessage
										id="offre.tarification.montant"
										defaultMessage="{montant} €"
										description="montant"
										values={{
											montant: <span>{thousandsSeparator(tarification.provisionsCharges, false)}</span>
										}}
									/>
								</Grid>
							</Grid>
							<Grid container item className={classes.modeReglement}>
								<Grid item className={classes.greyTextFin} xs={12}>
									<FormattedMessage
										id="offre.tarification.modeReglement"
										defaultMessage="Le mode de règlement des charges est par provisions avec régularisation annuelle"
										description="Provisions sur charges"
									/>
								</Grid>
							</Grid>
							{
								tarification.encadrementLoyer && tarification.encadrementLoyer === '1' &&
								<>
									<Grid item container className={classes.blocEncadrement}>
										<Grid item container className={classes.roundBlockContent}>
											<h3 className={classes.titleLoyerMensuel}>
												<FormattedMessage
													id="offre.tarification.loyerEncadre"
													defaultMessage="Zone soumise à encadrement de loyer"
													description="Zone soumise à encadrement de loyer"
												/>
											</h3>
										</Grid>
									</Grid>

									<Grid item container className={classes.referenceLoyer}>
										{/* Ligne loyer de base */}
										<Grid item container justifyContent="space-between" className={classes.loyerBase}>
											<Grid item className={classes.greyText} xs={8}>
												<FormattedMessage
													id="offre.tarification.loyerBase"
													defaultMessage="Loyer de base"
													description="Loyer de base"
												/>
											</Grid>
											<Grid item className={classes.greyTextRight} xs={4}>
												<FormattedMessage
													id="offre.tarification.montantM2"
													defaultMessage="{montant} € ({prixm2} €/m²)"
													description="montant"
													values={{
														montant: <span>{thousandsSeparator(tarification.loyerBase ?? 0, false)}</span>,
														prixm2: <span>{thousandsSeparator(tarification.loyerBasem2 ?? 0, false)}</span>
													}}
												/>
											</Grid>
										</Grid>

										{/* Ligne loyer de référence majoré */}
										<Grid item container justifyContent="space-between" className={classes.loyerBase}>
											<Grid item className={classes.greyText} xs={8}>
												<FormattedMessage
													id="offre.tarification.loyerMajore"
													defaultMessage="Loyer de référence majoré"
													description="Loyer de référence majoré"
												/>
											</Grid>
											<Grid item className={classes.greyTextRight} xs={4}>
												<FormattedMessage
													id="offre.tarification.montantM2"
													defaultMessage="{montant} € ({prixm2} €/m²)"
													description="montant"
													values={{
														montant: <span>{thousandsSeparator(tarification.loyerMajore ?? 0, false)}</span>,
														prixm2: <span>{thousandsSeparator(tarification.loyerMajorem2 ?? 0, false)}</span>
													}}
												/>
											</Grid>
										</Grid>

										{
											tarification.complementLoyer && tarification.complementLoyer === '1' && (
												// Ligne de complément de loyer
												<Grid item container justifyContent="space-between" className={classes.loyerBase}>
													<Grid item className={classes.greyText} xs={8}>
														<Grid item className={classes.toolTip}>
															<Tooltip
																title="Le loyer de cet appartement dépasse le loyer de référence majoré. Un complément de loyer se justifie généralement par des caractéristiques particulières (confort, terrasse, vue, équipements, etc.)."
																open={toolTipOpen}
																setOpen={setToolTipOpen}
															>
																{
																	toolTipOpen ? (
																		<InfoIcon className="tooltipOuvertSVG" />
																	) : (
																		<InfoOutlinedIcon />
																	)
																}
															</Tooltip>
														</Grid>

														<FormattedMessage
															id="offre.tarification.complementLoyer"
															defaultMessage="Complément de loyer"
															description="Complément de Loyer"
														/>
													</Grid>
													<Grid item className={classes.greyTextRight} xs={4}>
														<FormattedMessage
															id="offre.tarification.montant"
															defaultMessage="montant €²"
															description="montant"
															values={{
																montant: <span>{thousandsSeparator(tarification.complementLoyerValue ?? 0, false)}</span>
															}}
														/>
													</Grid>
												</Grid>
											)
										}
									</Grid>
								</>
							}

							<Grid item container className={classes.blocHonoraires}>
								<Grid container item xs={12} className={classes.titreHonoraires}>
									<FormattedMessage
										id="offre.tarification.titreHonoraires"
										defaultMessage="Le règlement initial comprend les honoraires et le dépôt de garantie :"
										description="Titre du bloc des honoraires"
									/>
								</Grid>

								<Grid item container className={classes.roundBlockContent}>
									<Grid item container justifyContent="space-between">
										<Grid item className={classes.titleLoyerMensuel} md={9} xs={8}>
											<FormattedMessage
												id="offre.tarification.honoraires"
												defaultMessage="Honoraires TTC à la charge du locataire"
												description="Honoraires TTC à la charge du locataire"
											/>
										</Grid>

										<Grid item className={classes.titleLoyerMensuelRight} md={3} xs={4}>
											<FormattedMessage
												id="offre.tarification.montant"
												defaultMessage="{montant} €"
												description="montant"
												values={{
													montant: <span>{thousandsSeparator(tarification.honoraires, false)}</span>
												}}
											/>
										</Grid>
									</Grid>

									{
										offre.type === lotTypesEnum.apartment && (
											<span className={classes.dont}>
												<FormattedMessage
													id="offre.tarification.dont"
													defaultMessage="Dont"
													description="dont"
												/>
											</span>
										)
									}
								</Grid>

								{
									offre.type === lotTypesEnum.apartment && (
										<Grid container item justifyContent="space-between" className={classes.etatDesLieux}>
											<Grid item className={classes.greyText} md={9} xs={8}>
												<FormattedMessage
													id="offre.tarification.edl"
													defaultMessage="État des lieux"
													description="État des lieux"
												/>
											</Grid>
											<Grid item className={classes.greyTextRight} md={3} xs={4}>
												<FormattedMessage
													id="offre.tarification.montant"
													defaultMessage="{montant} €"
													description="montant"
													values={{
														montant: <span>{thousandsSeparator(tarification.EDL, false)}</span>
													}}
												/>
											</Grid>
										</Grid>
									)
								}
							</Grid>
							<Grid item container className={classes.roundBlockContentBottom}>
								<Grid item container justifyContent="space-between">
									<Grid item className={classes.titleLoyerMensuel} md={9} xs={8}>
										<FormattedMessage
											id="offre.tarification.depotGarantie"
											defaultMessage="Dépôt de garantie (un mois de loyer hors charges)"
											description="Dépôt de garantie (un mois de loyer hors charges)"
										/>
									</Grid>
									<Grid item className={classes.titleLoyerMensuelRight} md={3} xs={4}>
										<FormattedMessage
											id="offre.tarification.montant"
											defaultMessage="{montant} €"
											description="montant"
											values={{
												montant: <span>{thousandsSeparator(tarification.depotGarantie, false)}</span>
											}}
										/>
									</Grid>
								</Grid>
								<Grid container item className={classes.titleLoyerMensuel}>
									<Grid item className={classes.greyTextFin} xs={12}>
										<FormattedMessage
											id="offre.tarification.georisques"
											defaultMessage="Les informations sur les risques auxquels ce bien est exposé sont disponibles sur le site Géorisques : www.georisques.gouv.fr"
											description="Informations risques"
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						}
					</Grid>
				</Grid>
			</Grid>
		</Element>
	)
}

export default ConditionsTarifaires